import React, { useEffect, useRef, useState } from "react";
import Papa from "../../../assets/images/camera.png";
import toast from "react-hot-toast";
import {
  updateProfile,
  getProfileInfo,
  updateWebhookUrl,
} from "../../../services/settings";
import { useQueryClient } from "@tanstack/react-query";
import Password from "./Password";
import { useAuth } from "../../../context/AuthContextProvider";

const ProfileSetting = () => {
  const { globalProfile } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [file, setFile] = useState(null);
  const [modalPass, setModalPass] = useState();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [isWebhookLive, setIsWebhookLive] = useState(
    globalProfile === "PRODUCTION"
  );
  const [profileLoading, setProfileLoading] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
        // Convert the file to a binary string
        // const binaryString = reader.result.split(",")[1];
        //  Append the binary string to the file field
        // setFile(binaryString);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = (e) => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append("firstName", firstName);
      formData.append("lastName", lastName);
      formData.append("emailAddress", emailAddress);
      formData.append("phoneNumber", phoneNumber);
      formData.append("file", file);

      const response = await updateProfile(formData);
      toast.success(response.message);
      queryClient.invalidateQueries({ queryKey: ["profileInfo"] });
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Unable to update profile");
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const handleWebhookSubmit = async (e) => {
    e.preventDefault();
    const trimmedUrl = webhookUrl.trim();

    try {
      new URL(trimmedUrl);
    } catch (error) {
      toast.error("Enter a valid webhook URL");
      return;
    }

    setWebhookLoading(true);
    try {
      const response = await updateWebhookUrl({
        url: trimmedUrl,
        isLive: isWebhookLive,
      });
      toast.success(response.message || "Webhook URL updated successfully");
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Unable to update webhook URL");
      }
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleProfileInfo = async () => {
    try {
      const res = await getProfileInfo();
      setFirstName(res.data.firstName); // Update state variables with profileInfo data
      setLastName(res.data.lastName);
      setEmailAddress(res.data.email);
      setPhoneNumber(res.data.phoneNumber);
      if (res.data.base64File) {
        // Convert binary data to a data URL
        const dataUrl = `data:image/jpeg;base64,${res.data.base64File}`;
        setPreviewImage(dataUrl);
      }
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  useEffect(() => {
    handleProfileInfo();
  }, [globalProfile]);

  useEffect(() => {
    setIsWebhookLive(globalProfile === "PRODUCTION");
  }, [globalProfile]);

  return (
    <div className="md:pl-[2.5rem] pt-[1rem] pb-10">
      <h2 className="text-xl font-bold mb-4">Change Default Display Picture</h2>

      <form encType="multipart/form-data" onSubmit={handleSubmit}>
        <input
          type="file"
          id="upload"
          name="upload"
          accept="image/*"
          className="hidden"
          ref={fileInputRef}
          onChange={handleImageChange}
        />
        <div
          className="w-[212px] h-[174px] flex flex-col items-center justify-center relative"
          onClick={handleImageClick}
        >
          {previewImage ? (
            <img
              src={previewImage}
              alt="Preview"
              className="w-[114px] h-[114px]
             rounded-full"
            />
          ) : (
            <div className="w-[114px] h-[114px] bg-gray-500/15 rounded-full relative">
              <img
                src={Papa}
                alt="preview"
                className="w-[38.4px] h-[31.74px] absolute top-9 right-9"
              />
            </div>
          )}
          <p className="text-sm text-center w-[212px] h-[45px] mt-3 font-normal text-darker-grey">
            *The uploaded image must be 500px wide and 500px long
          </p>
        </div>

        <section className="md:w-[716px] mt-[3rem] grid lg:grid-cols-2 gap-x-4 gap-y-6 px-2 md:px-0">
          <div className="flex flex-col">
            <label>First Name</label>
            <input
              // placeholder="Update first name"
              value={firstName}
              type="text"
              className="xl:w-[348px] w-80 h-[50px] bg-gray-100 outline-none rounded-[.5rem] md:pl-[.5rem] pl-2 flex justify-center items-center mt-3"
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label>Last Name</label>
            <input
              // placeholder="Update last name"
              value={lastName}
              type="text"
              className="xl:w-[348px] w-80 h-[50px] bg-gray-100 outline-none rounded-[.5rem] md:pl-[.5rem] pl-2 flex justify-center items-center mt-3"
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label>Email Address</label>
            <input
              disabled
              value={emailAddress}
              // placeholder="Update email address"
              // onChange={(e) => setEmailAddress(e.target.value)}
              type="text"
              className="xl:w-[348px] w-80 h-[50px] bg-gray-100 outline-none rounded-[.5rem] md:pl-[.5rem] pl-2 flex justify-center items-center mt-3"
            />
          </div>

          <div className="flex flex-col">
            <label>Phone Number</label>
            <input
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              // placeholder="Update phone number"
              type="text"
              className="xl:w-[348px] w-80 h-[50px] bg-gray-100 outline-none rounded-[.5rem] md:pl-[.5rem] pl-2 flex justify-center items-center mt-3"
            />
          </div>
        </section>

        <section className="mt-10 md:w-[716px] flex flex-col sm:flex-row gap-4 justify-center sm:justify-start px-2 md:px-0">
          {/* <div className="flex items-center gap-2">
            <input type="checkbox" />
            <p>
              I have read the <b>Terms and Conditions</b>
            </p>
          </div> */}

          <button
            type="button"
            onClick={() => setModalPass(true)}
            className="w-[272px] h-[42px] rounded-[8px] text-white bg-primary"
          >
            Change Password
          </button>
          <button
            type="submit"
            disabled={profileLoading}
            className="w-[272px] h-[42px] rounded-[8px] text-white bg-primary disabled:cursor-not-allowed disabled:opacity-70"
          >
            {profileLoading ? "Updating..." : "Update"}
          </button>
        </section>
      </form>

      <form
        onSubmit={handleWebhookSubmit}
        className="md:w-[716px] mt-12 mx-2 md:mx-0 rounded-[8px] border border-gray-200 bg-white p-5"
      >
        <div className="flex flex-col gap-2 border-b border-gray-100 pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold text-navy-blue">Webhook URL</h2>
            <p className="mt-1 text-sm text-gray-500">
              Set the endpoint that receives payment and transaction events.
            </p>
          </div>

          <label className="flex w-fit cursor-pointer items-center gap-3 rounded-full border border-gray-200 bg-gray-50 px-3 py-2">
            <span
              className={`text-sm font-semibold ${
                isWebhookLive ? "text-primary" : "text-gray-500"
              }`}
            >
              {isWebhookLive ? "Live" : "Test"}
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={isWebhookLive}
              onChange={(e) => setIsWebhookLive(e.target.checked)}
            />
            <span
              className={`relative h-6 w-11 rounded-full transition ${
                isWebhookLive ? "bg-primary" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${
                  isWebhookLive ? "left-6" : "left-1"
                }`}
              />
            </span>
          </label>
        </div>

        <div className="mt-5 flex flex-col">
          <label htmlFor="webhook-url" className="font-semibold text-gray-700">
            Endpoint URL
          </label>
          <input
            id="webhook-url"
            type="url"
            value={webhookUrl}
            placeholder="https://example.com/payloft/webhook"
            className="mt-3 h-[50px] w-full rounded-[8px] bg-gray-100 px-3 text-gray-700 outline-none focus:ring-2 focus:ring-primary/30"
            onChange={(e) => setWebhookUrl(e.target.value)}
            required
          />
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={webhookLoading}
            className="h-[42px] w-[180px] rounded-[8px] bg-primary text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {webhookLoading ? "Saving..." : "Save Webhook"}
          </button>
        </div>
      </form>
      {modalPass && <Password setIsModalOpen={setModalPass} />}
    </div>
  );
};

export default ProfileSetting;
